/**
 * Rattrape l'état des boutons wishlist injectés APRÈS le chargement.
 *
 * L'app Advanced Wishlist (Wishlist Power) ne renseigne `aria-checked` que lors
 * d'une passe unique au boot, puis sur ses propres events
 * `ooo-wishlist:product-added` / `-removed` — ceux-là rebalayent bien tout le
 * DOM, y compris les cartes injectées. Ce qui manque, c'est **l'état initial**
 * d'un `<ooo-wl-wishlist-button>` inséré plus tard : carte de recommandation
 * fetchée (`nosto-dynamic-card`), quick-add, morph de section… Le cœur reste
 * vide alors que le produit est bien en wishlist.
 *
 * Constaté à l'inspection de l'app : son `connectedCallback` ne lit pas l'état.
 * Ni un élément neuf construit après coup, ni la réinsertion d'un élément
 * existant ne déclenchent de synchronisation. Et sa documentation développeur
 * n'expose aucune API de re-scan. On fait donc le rattrapage ici, à partir de
 * `getContent()` (API publique documentée).
 *
 * Deux garde-fous rendent ce module incapable de désynchroniser l'app :
 *   - on n'écrit **jamais** `aria-checked="false"` — seulement `true`, ce qui est
 *     purement additif par rapport à l'état rendu par défaut ;
 *   - un élément déjà coché, ou en cours de requête (`aria-busy`), est laissé
 *     tranquille.
 */
(() => {
  const BUTTON_TAG = 'ooo-wl-wishlist-button';
  const SYNCED_ATTR = 'data-wl-synced';
  const API_TIMEOUT = 8000;
  const API_POLL_INTERVAL = 200;

  /** Contenu de la wishlist mis en cache, invalidé à chaque changement. */
  let contentPromise = null;
  let flushScheduled = false;

  const invalidate = () => {
    contentPromise = null;
  };

  /**
   * L'app se charge en asynchrone (app embed) : on attend son API.
   * @returns {Promise<{ getContent: () => Promise<{ products?: Array<Record<string, unknown>> }> } | null>}
   */
  function waitForWishlist() {
    return new Promise((resolve) => {
      const start = performance.now();
      const check = () => {
        const wishlist = window.Maestrooo?.wishlist;
        if (wishlist?.getContent) {
          resolve(wishlist);
          return;
        }
        if (performance.now() - start > API_TIMEOUT) {
          resolve(null);
          return;
        }
        setTimeout(check, API_POLL_INTERVAL);
      };
      check();
    });
  }

  /**
   * Clés présentes dans la wishlist : `handle` pour un suivi produit,
   * `handle:variantId` pour un suivi variante (l'app gère les deux).
   * @returns {Promise<Set<string>>}
   */
  function getWishlistKeys() {
    contentPromise ??= (async () => {
      const wishlist = await waitForWishlist();
      if (!wishlist) return new Set();

      const keys = new Set();
      try {
        const content = await wishlist.getContent();
        for (const product of content?.products ?? []) {
          const handle = product.handle;
          if (!handle) continue;
          keys.add(String(handle));
          if (product.variant_id != null) keys.add(`${handle}:${product.variant_id}`);
        }
      } catch {
        // Wishlist illisible : on ne coche rien, l'app garde la main.
      }
      return keys;
    })();

    return contentPromise;
  }

  /** Coche les boutons pas encore synchronisés dont le produit est en wishlist. */
  async function flush() {
    flushScheduled = false;

    const pending = document.querySelectorAll(`${BUTTON_TAG}:not([${SYNCED_ATTR}])`);
    if (!pending.length) return;

    const keys = await getWishlistKeys();

    for (const element of pending) {
      element.setAttribute(SYNCED_ATTR, '');

      const button = element.querySelector('button');
      if (!button) continue;
      // L'app a déjà tranché, ou une requête est en vol : on ne touche à rien.
      if (button.getAttribute('aria-checked') === 'true' || button.getAttribute('aria-busy') === 'true') continue;

      const handle = element.getAttribute('handle');
      if (!handle) continue;
      const variantId = element.getAttribute('variant-id');
      const key = variantId ? `${handle}:${variantId}` : handle;

      if (keys.has(key)) button.setAttribute('aria-checked', 'true');
    }
  }

  function scheduleFlush() {
    if (flushScheduled) return;
    flushScheduled = true;
    requestAnimationFrame(flush);
  }

  /** Un lot d'ajouts DOM contient-il un bouton wishlist ? */
  function containsWishlistButton(nodes) {
    for (const node of nodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const element = /** @type {Element} */ (node);
      if (element.localName === BUTTON_TAG || element.querySelector(BUTTON_TAG)) return true;
    }
    return false;
  }

  // On observe les insertions plutôt que de patcher chaque injecteur : les cartes
  // arrivent de partout (recommandations Nosto, quick-add, section-renderer) et un
  // nouvel injecteur bénéficiera du rattrapage sans y penser.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length && containsWishlistButton(mutation.addedNodes)) {
        scheduleFlush();
        return;
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Le cache doit suivre les changements de wishlist : l'app met à jour le DOM
  // existant sur ces events, mais une carte injectée ENSUITE doit voir le nouvel
  // état. On invalide, sans re-flush (rien de neuf dans le DOM à cet instant).
  document.addEventListener('ooo-wishlist:product-added', invalidate);
  document.addEventListener('ooo-wishlist:product-removed', invalidate);

  // Filet : si ce module se charge après une première injection (defer + fetch
  // rapide), l'observer l'a manquée. Un balayage au démarrage la rattrape.
  scheduleFlush();
})();
